import React, { useState, useCallback, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { PageWrapper } from '../components/partials';
import Reports from '../services/reports';
import { useUrlParams } from '../utils/useUrlParamsHook';
import css from './page.css';
import Sunburst from './components/sunburst/Sunburst';
import AnalysisSidebar from './components/AnalysisSidebar/AnalysisSidebar';
import SaveReportButton from './components/SaveReport/SaveReportButton';
import RcaView from './components/RcaView';
import useAuthedChat from '../chatView/hooks/useAuthedChat';
import useSessionUrlSync from '../chatView/hooks/useSessionUrlSync';
import StreamMonitor from '../chatView/components/StreamMonitor';
import SessionNoticeToasts from '../chatView/components/SessionNoticeToasts';
import { getAdminModeEnabled } from '../selectors/user';
import { decodeBookmarkName } from '../topbar/bookmarkUtils';
import {
    buildRingQueryParams,
    getMetricColumnKey,
    getMetricRcaFields,
    getReportRows,
    buildRingPoints,
    buildQueryKey,
    assignGlobalColors,
    buildRcaPayload,
} from './queryUtils';
import {
    ANOMALY_EVENTS,
    ChartColorConfig,
    MAX_CACHE_ENTRIES,
    RCA_SOURCES,
    SidebarTabs,
    buildRcaUserMessage,
    formatChangeValue,
    trackAnomalyEvent,
} from './consts';
import { CONTEXT_PROD_MODE, SESSION_TYPES, SUBMIT_STATUS } from '../chatView/constants';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const getPeriodDays = period => {
    if (!period?.start || !period?.end) return null;
    const ms = new Date(period.end) - new Date(period.start);
    return Number.isFinite(ms) ? Math.round(ms / MS_PER_DAY) + 1 : null;
};

const countActiveFilters = filters => (filters || []).filter(f => f.values?.length).length;

const reportsService = new Reports();

const findMetricMeta = (rawMetadata, columnKey) => {
    const baseName = columnKey?.split('__')[0];
    const entry = rawMetadata[baseName];
    if (!entry) return null;
    if (!entry.columns) return entry;
    const period = columnKey.split('__').slice(1).join('__');
    const subColumn = entry.columns.find(col => col.name === period);
    return subColumn ? { ...entry, display_name: subColumn.display_name } : entry;
};

const extractMetricsMeta = (rawMetadata, colorKey, sizeKey) => ({
    colorMeta: findMetricMeta(rawMetadata, colorKey),
    sizeMeta: findMetricMeta(rawMetadata, sizeKey),
});

const getMetricLabel = meta => meta?.display_name || meta?.name?.toUpperCase() || '';

const resolveChartColors = increasesAreGood => ({
    positive: increasesAreGood ? ChartColorConfig.GOOD : ChartColorConfig.BAD,
    negative: increasesAreGood ? ChartColorConfig.BAD : ChartColorConfig.GOOD,
    neutral: ChartColorConfig.NEUTRAL,
    noData: ChartColorConfig.NO_DATA,
});

const AnomalyAnalysisPage = () => {
    const { getParamsObject, setParamsObject } = useUrlParams();
    const [initialUrlParams] = useState(getParamsObject);
    const [queryResult, setQueryResult] = useState(null);
    const [lastRunParams, setLastRunParams] = useState(null);
    const [activeBookmark, setActiveBookmark] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(false);
    const [isRcaOpen, setIsRcaOpen] = useState(false);
    const [contextEnv, setContextEnv] = useState(CONTEXT_PROD_MODE);
    const [activeTab, setActiveTab] = useState(SidebarTabs.SETUP);
    const [pendingFirstMessage, setPendingFirstMessage] = useState(null);

    const isAdminMode = useSelector(getAdminModeEnabled);

    const { chat, authLoading, authError } = useAuthedChat({
        runPath: 'runs',
        sessionType: SESSION_TYPES.RCA,
        contextEnv: isAdminMode ? contextEnv : CONTEXT_PROD_MODE,
        adminMode: isAdminMode,
        isViewingConversation: isRcaOpen,
    });

    const {
        currentSessionId,
        handleSubmit,
        clearCurrentSession,
        loadSession,
        loadingSessionId,
        streamingSessionIds,
        updatedSessionIds,
        sessions,
        isLoadingSessions,
        sessionsLoadError,
        deleteSession,
        updateSessionTitle,
        fetchSessions,
        streamDescriptors,
        handleStreamMessage,
        handleStreamError,
    } = chat;

    const latestRunRef = useRef(0);
    const cacheRef = useRef(new Map());

    const buildResult = (reportDataList, paramsList, queryConfig) => {
        const colorKey = getMetricColumnKey(queryConfig.colorMetric.value);
        const sizeKey = getMetricColumnKey(queryConfig.sizeMetric.value);

        const ringRows = reportDataList.map(getReportRows);
        const rawMetadata = reportDataList[0].metadata || {};
        const { colorMeta, sizeMeta } = extractMetricsMeta(rawMetadata, colorKey, sizeKey);
        const metaIncreasesAreGood = colorMeta?.increases_are_good ?? true;

        const rawPoints = ringRows.flatMap((rows, ringLevel) => {
            const apiDimKeys = paramsList[ringLevel].dimensions;
            return buildRingPoints(rows, apiDimKeys, sizeKey, colorKey, ringLevel);
        });
        const allPoints = assignGlobalColors(rawPoints, metaIncreasesAreGood);

        const colorRcaFields = getMetricRcaFields(queryConfig.colorMetric.value);

        return {
            data: allPoints,
            chartColors: resolveChartColors(metaIncreasesAreGood),
            colorMetricApiName: colorRcaFields.api_name,
            colorMetricCohortPeriod: colorRcaFields.cohort_period,
            firstPeriod: queryConfig.firstPeriod,
            secondPeriod: queryConfig.secondPeriod,
            dimensions: queryConfig.dimensions,
            filters: queryConfig.filters,
            increasesAreGood: metaIncreasesAreGood,
            metricLabels: {
                color: getMetricLabel(colorMeta),
                size: getMetricLabel(sizeMeta),
            },
            displayFormats: {
                color: colorMeta?.display_format || null,
                size: sizeMeta?.display_format || null,
            },
        };
    };

    const handleRun = useCallback(async queryConfig => {
        const params = {
            dimensions: queryConfig.dimensions.map(d => d.value),
            colorMetric: queryConfig.colorMetric?.value,
            sizeMetric: queryConfig.sizeMetric?.value,
            filters: queryConfig.filters,
            firstPeriod: queryConfig.firstPeriod,
        };
        setParamsObject(params);
        setLastRunParams(params);

        const runId = ++latestRunRef.current;
        const key = buildQueryKey(queryConfig);
        const cached = cacheRef.current.get(key);

        const runProps = {
            dimensions: params.dimensions,
            dimensionCount: params.dimensions.length,
            colorMetric: params.colorMetric,
            sizeMetric: params.sizeMetric,
            periodDays: getPeriodDays(queryConfig.firstPeriod),
            filterCount: countActiveFilters(queryConfig.filters),
        };
        trackAnomalyEvent(ANOMALY_EVENTS.RUN_QUERY, { ...runProps, fromCache: !!cached });

        if (cached) {
            setIsLoading(false);
            setError(false);
            setQueryResult(buildResult(cached.reportDataList, cached.paramsList, queryConfig));
            return;
        }
        setIsLoading(true);
        setError(false);
        try {
            const paramsList = buildRingQueryParams(queryConfig);
            const reportDataList = await Promise.all(
                paramsList.map((params, i) => reportsService.runQuery(params, { suffix: `ring${i}` }))
            );
            if (runId !== latestRunRef.current) return;

            if (cacheRef.current.size >= MAX_CACHE_ENTRIES) {
                cacheRef.current.delete(cacheRef.current.keys().next().value);
            }
            cacheRef.current.set(key, { reportDataList, paramsList });
            setQueryResult(buildResult(reportDataList, paramsList, queryConfig));
        } catch (err) {
            if (runId !== latestRunRef.current) return;
            setError(true);
            trackAnomalyEvent(ANOMALY_EVENTS.RUN_FAILED, runProps);
        } finally {
            if (runId === latestRunRef.current) setIsLoading(false);
        }
    }, []);
    
    useEffect(() => {
        if (!pendingFirstMessage || currentSessionId) return;
        handleSubmit(null, { content: pendingFirstMessage }).then(status => {
            if (status !== SUBMIT_STATUS.REJECTED) setPendingFirstMessage(null);
        });
    }, [pendingFirstMessage, currentSessionId, handleSubmit]);

    const handleRunRca = useCallback((pointData, source = RCA_SOURCES.CHART_SEGMENT) => {
        if (!queryResult || authLoading || authError || loadingSessionId) return;
        const payload = buildRcaPayload(queryResult, pointData);
        trackAnomalyEvent(ANOMALY_EVENTS.RCA_RUN, {
            metric: payload.metric?.api_name,
            pctChange: payload.pct_change,
            depth: pointData.custom?.dimValues?.length ?? 0,
            filterCount: payload.filters.length,
            source,
        });
        clearCurrentSession();
        setPendingFirstMessage(buildRcaUserMessage(payload));
        setActiveTab(SidebarTabs.SETUP);
        setIsRcaOpen(true);
    }, [queryResult, authLoading, authError, loadingSessionId, clearCurrentSession]);

    const openSession = useCallback(
        async sessionId => {
            setActiveTab(SidebarTabs.HISTORY);
            if (sessionId === currentSessionId) {
                setIsRcaOpen(true);
                return true;
            }
            const loadedMessages = await loadSession(sessionId);
            if (loadedMessages) setIsRcaOpen(true);
            return !!loadedMessages;
        },
        [currentSessionId, loadSession]
    );

    const handleSessionSelect = useCallback(
        async sessionId => {
            if (loadingSessionId) return;
            trackAnomalyEvent(ANOMALY_EVENTS.HISTORY_SESSION_OPEN, { sessionId });
            await openSession(sessionId);
        },
        [loadingSessionId, openSession]
    );

    const { urlSessionId, setSessionParam } = useSessionUrlSync({
        ready: !authLoading && !authError,
        openSessionId: isRcaOpen ? currentSessionId : null,
        onOpenFromUrl: sessionId => {
            openSession(sessionId).then(opened => {
                if (!opened) setSessionParam(null);
            });
        },
        onUrlCleared: () => setIsRcaOpen(false),
        pauseStateSync: !!loadingSessionId,
    });

    const handleRcaBack = () => {
        trackAnomalyEvent(ANOMALY_EVENTS.RCA_CLOSE, {});
        setIsRcaOpen(false);
    };

    return (
        <PageWrapper className={css.pageWrapper} disablePadding>
            {streamDescriptors.map(({ key, runId }) => (
                <StreamMonitor
                    key={runId}
                    sessionKey={key}
                    runId={runId}
                    onMessage={handleStreamMessage}
                    onError={handleStreamError}
                />
            ))}
            <div className={css.body}>
                <SessionNoticeToasts
                    chat={chat}
                    onSessionGone={sessionId => {
                        if (sessionId === urlSessionId) setSessionParam(null);
                    }}
                />
                {isRcaOpen && (
                    <RcaView
                        chat={chat}
                        onBack={handleRcaBack}
                        contextEnv={contextEnv}
                        onContextEnvChange={setContextEnv}
                    />
                )}
                <div className={classNames(css.sunburstView, { [css.hiddenView]: isRcaOpen })}>
                    <AnalysisSidebar
                        activeTab={activeTab}
                        onTabChange={setActiveTab}
                        onRun={handleRun}
                        initialUrlParams={initialUrlParams}
                        hasQueried={!!queryResult}
                        sessions={sessions}
                        isLoadingSessions={isLoadingSessions || authLoading}
                        sessionsLoadError={sessionsLoadError || authError}
                        loadingSessionId={loadingSessionId}
                        streamingSessionIds={streamingSessionIds}
                        updatedSessionIds={updatedSessionIds}
                        onSessionSelect={handleSessionSelect}
                        onSessionDelete={deleteSession}
                        onSessionRename={updateSessionTitle}
                        onRetry={fetchSessions}
                    />
                    <div className={css.content}>
                        <Sunburst
                            data={queryResult?.data}
                            isLoading={isLoading}
                            error={error}
                            hasQueried={!!queryResult}
                            chartColors={queryResult?.chartColors}
                            metricLabels={queryResult?.metricLabels}
                            displayFormats={queryResult?.displayFormats}
                            increasesAreGood={queryResult?.increasesAreGood}
                            onRunRca={handleRunRca}
                            firstPeriod={queryResult?.firstPeriod}
                            secondPeriod={queryResult?.secondPeriod}
                            contextEnv={contextEnv}
                            onContextEnvChange={setContextEnv}
                            activeBookmarkName={activeBookmark ? decodeBookmarkName(activeBookmark.name) : ''}
                            headerActions={
                                <SaveReportButton
                                    queryParams={lastRunParams}
                                    activeBookmark={activeBookmark}
                                    onActiveBookmarkChange={setActiveBookmark}
                                />
                            }
                        />
                    </div>
                </div>
            </div>
        </PageWrapper>
    );
};

export default AnomalyAnalysisPage;
